import React, { useState, useEffect } from "react";
import "./Profile.css";

const Profile = () => {
  // Load user data from login (localStorage)
  const [profileData, setProfileData] = useState(() => {
    const userName = localStorage.getItem("userName");
    const userEmail = localStorage.getItem("userEmail");
    const savedPhone = localStorage.getItem("profilePhone");
    
    return {
      name: userName || localStorage.getItem("profileName") || "Guest",
      email: userEmail || localStorage.getItem("profileEmail") || "",
      phone: savedPhone || ""
    };
  });

  // Load saved addresses for current user
  const [addresses, setAddresses] = useState(() => {
    const userEmail = localStorage.getItem("userEmail") || profileData.email;
    const savedAddresses = localStorage.getItem(`profileAddresses_${userEmail}`);
    return savedAddresses ? JSON.parse(savedAddresses) : [];
  });

  const [showAddressPage, setShowAddressPage] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [tempName, setTempName] = useState(profileData.name);
  const [tempPhone, setTempPhone] = useState(profileData.phone);
  
  // Address form state
  const [addressForm, setAddressForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    country: "India",
    postalCode: "",
    phone: ""
  });

  // Save profile data to localStorage
  useEffect(() => {
    localStorage.setItem("profileName", profileData.name);
    localStorage.setItem("profilePhone", profileData.phone);
    localStorage.setItem("profileEmail", profileData.email);
    
    // Also update userName if it's from profile
    if (profileData.name !== "Guest") {
      localStorage.setItem("userName", profileData.name);
    }
  }, [profileData]);

  // Save addresses to localStorage for current user
  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail") || profileData.email;
    if (userEmail) {
      localStorage.setItem(`profileAddresses_${userEmail}`, JSON.stringify(addresses));
    }
  }, [addresses, profileData.email]);

  // Get default address
  const getDefaultAddress = () => {
    return addresses.find(addr => addr.isDefault) || addresses[0];
  };

  const handleUpdateProfile = () => {
    const updatedData = {
      ...profileData,
      name: tempName,
      phone: tempPhone
    };
    
    setProfileData(updatedData);
    
    // Update localStorage
    localStorage.setItem("userName", tempName);
    localStorage.setItem("profileName", tempName);
    localStorage.setItem("profilePhone", tempPhone);
    
    setIsEdit(false);
  };

  const handleAddAddressClick = () => {
    setShowAddressPage(true);
    setShowAddressForm(true);
    setAddressForm({
      firstName: profileData.name.split(' ')[0] || "",
      lastName: profileData.name.split(' ').slice(1).join(' ') || "",
      company: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "India",
      postalCode: "",
      phone: profileData.phone || ""
    });
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (e) => {
    setAddressForm(prev => ({
      ...prev,
      country: e.target.value
    }));
  };

  const handleStateChange = (e) => {
    setAddressForm(prev => ({
      ...prev,
      state: e.target.value
    }));
  };

  const handleAddAddress = () => {
    // Validate required fields
    if (!addressForm.firstName.trim() || !addressForm.address1.trim() || 
        !addressForm.city.trim() || !addressForm.postalCode.trim()) {
      alert("Please fill in all required fields (First Name, Address, City, Postal Code)");
      return;
    }

    const newAddress = {
      id: Date.now(),
      fullName: `${addressForm.firstName} ${addressForm.lastName}`.trim(),
      address1: addressForm.address1,
      address2: addressForm.address2,
      city: addressForm.city,
      state: addressForm.state,
      country: addressForm.country,
      postalCode: addressForm.postalCode,
      phone: addressForm.phone || profileData.phone,
      company: addressForm.company,
      isDefault: addresses.length === 0
    };

    setAddresses(prev => {
      let updatedAddresses;
      if (newAddress.isDefault) {
        updatedAddresses = [newAddress, ...prev];
      } else {
        updatedAddresses = [...prev, newAddress];
      }
      return updatedAddresses;
    });
    
    // Close address form
    setShowAddressPage(false);
    setShowAddressForm(false);
    alert("Address added successfully!");
  };

  const handleEditAddress = (address) => {
    setShowAddressPage(true);
    setShowAddressForm(true);
    
    const nameParts = address.fullName.split(' ');
    setAddressForm({
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(' ') || "",
      company: address.company || "",
      address1: address.address1 || "",
      address2: address.address2 || "",
      city: address.city || "",
      state: address.state || "",
      country: address.country || "India",
      postalCode: address.postalCode || "",
      phone: address.phone || ""
    });
    
    // Remove old address
    setAddresses(prev => prev.filter(addr => addr.id !== address.id));
  };

  const handleCancelAddress = () => {
    setShowAddressPage(false);
    setShowAddressForm(false);
  };

  const handleSetDefaultAddress = (id) => {
    setAddresses(prev => prev.map(addr => ({
      ...addr,
      isDefault: addr.id === id
    })));
  };

  const handleRemoveAddress = (id) => {
    if (addresses.length <= 1) {
      alert("You must have at least one address");
      return;
    }
    
    const updatedAddresses = addresses.filter(addr => addr.id !== id);
    if (addresses.find(addr => addr.id === id)?.isDefault && updatedAddresses.length > 0) {
      updatedAddresses[0].isDefault = true;
    }
    
    setAddresses(updatedAddresses);
    alert("Address removed successfully!");
  };

  const handleViewAllAddresses = () => {
    setShowAddressPage(true);
    setShowAddressForm(false);
  };

  /* ================= ADDRESS PAGE ================= */
  if (showAddressPage) {
    return (
      <div className="address-page-container">
        <div className="address-page-header">
          <button
            className="back-to-profile"
            onClick={() => {
              setShowAddressPage(false);
              setShowAddressForm(false);
            }}
          >
            ←
          </button>
          <h2 className="section-title">
            {showAddressForm ? "Add a new address" : "Your Addresses"}
          </h2>
        </div>

        {!showAddressForm ? (
          /* ================= ADDRESSES LIST PAGE ================= */
          <div className="addresses-container">
            <div className="addresses-header">
              <button
                className="shopify-btn small"
                onClick={() => {
                  setShowAddressForm(true);
                }}
                style={{ marginBottom: '20px' }}
              >
                + Add new address
              </button>
            </div>

            {addresses.length === 0 ? (
              <p className="no-address">No addresses saved yet.</p>
            ) : (
              addresses.map(address => (
                <div key={address.id} className="address-card">
                  <div className="address-card-header">
                    {address.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                    <h3 className="address-name">{address.fullName}</h3>
                  </div>
                  <p className="address-details">
                    {address.address1}<br />
                    {address.address2 && <>{address.address2}<br /></>}
                    {address.city}, {address.state} {address.postalCode}<br />
                    {address.country}<br />
                    {address.phone && <>Phone: {address.phone}</>}
                  </p>
                  
                  <div className="address-card-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEditAddress(address)}
                    >
                      Edit
                    </button>
                    {!address.isDefault && (
                      <>
                        <button 
                          className="action-btn default"
                          onClick={() => handleSetDefaultAddress(address.id)}
                        >
                          Set as Default
                        </button>
                        <button 
                          className="action-btn remove"
                          onClick={() => handleRemoveAddress(address.id)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
            
            <p className="return-to-account" onClick={() => setShowAddressPage(false)}>
              ← Return to Account details
            </p>
          </div>
        ) : (
          /* ================= ADD ADDRESS FORM ================= */
          <div className="address-form-page">
            <div className="popup-form">
              <div className="two-col">
                <input 
                  name="firstName"
                  placeholder="First name *" 
                  value={addressForm.firstName}
                  onChange={handleAddressInputChange}
                  required
                />
                <input 
                  name="lastName"
                  placeholder="Last name" 
                  value={addressForm.lastName}
                  onChange={handleAddressInputChange}
                />
              </div>

              <input 
                name="company"
                placeholder="Company" 
                value={addressForm.company}
                onChange={handleAddressInputChange}
              />
              <input 
                name="address1"
                placeholder="Address 1 *" 
                value={addressForm.address1}
                onChange={handleAddressInputChange}
                required
              />
              <input 
                name="address2"
                placeholder="Address 2" 
                value={addressForm.address2}
                onChange={handleAddressInputChange}
              />
              <input 
                name="city"
                placeholder="City *" 
                value={addressForm.city}
                onChange={handleAddressInputChange}
                required
              />

              <div className="two-col">
                <input 
                  name="state"
                  placeholder="State / Province" 
                  value={addressForm.state}
                  onChange={handleAddressInputChange}
                />
                <input 
                  name="postalCode"
                  placeholder="Postal / ZIP code *" 
                  value={addressForm.postalCode}
                  onChange={handleAddressInputChange}
                  required
                />
              </div>

              <select value={addressForm.country} onChange={handleCountryChange}>
                <option value="India">India</option>
                <option value="USA">United States</option>
                <option value="UK">United Kingdom</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
              </select>

              <input 
                name="phone"
                placeholder="Phone" 
                value={addressForm.phone}
                onChange={handleAddressInputChange}
              />
              
              <div className="form-note">
                <p>* Required fields</p>
              </div>
            </div>

            {/* FORM ACTION BUTTONS */}
            <div className="popup-actions">
              <button
                className="shopify-btn small"
                onClick={handleCancelAddress}
              >
                Cancel
              </button>

              <button 
                className="shopify-btn small primary"
                onClick={handleAddAddress}
              >
                {addressForm.firstName ? "Update Address" : "Add address"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ================= PROFILE PAGE ================= */
  const defaultAddress = getDefaultAddress();

  return (
    <>
      <h2 className="section-title">Profile</h2>
      <hr />

      <div className="profile-info">
        <div className="info-block">
          <span className="icon">👤</span>
          <div>
            <label>Name</label>
            {isEdit ? (
              <input
                className="edit-input"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
              />
            ) : (
              <p>{profileData.name}</p>
            )}
          </div>
        </div>

        <div className="info-block">
          <span className="icon">📧</span>
          <div>
            <label>Email Address</label>
            <p>{profileData.email}</p>
          </div>

          <button className="edit-btn" onClick={() => setIsEdit(!isEdit)}>
            ✏️
          </button>
        </div>

        <div className="info-block">
          <span className="icon">📞</span>
          <div>
            <label>Phone</label>
            {isEdit ? (
              <>
                <input
                  className="edit-input"
                  value={tempPhone}
                  onChange={(e) => setTempPhone(e.target.value)}
                />
                <button
                  className="update-btn"
                  onClick={handleUpdateProfile}
                >
                  Update
                </button>
              </>
            ) : (
              <p>{profileData.phone || "Not provided"}</p>
            )}
          </div>
        </div>
      </div>

      <hr />

      <div className="address-header">
        <h3>Address</h3>
        <div className="address-buttons">
          <button
            className="shopify-btn small"
            onClick={handleAddAddressClick}
          >
            Add new address
          </button>
          {addresses.length > 1 && (
            <button
              className="shopify-btn small"
              onClick={handleViewAllAddresses}
              style={{ marginLeft: '10px' }}
            >
              View all ({addresses.length})
            </button>
          )}
        </div>
      </div>

      {addresses.length === 0 ? (
        <p className="no-address">No shipping addresses available. Add your first address.</p>
      ) : (
        <div className="addresses-list">
          {defaultAddress && (
            <div key={defaultAddress.id} className="address-card">
              <div className="address-card-header">
                <span className="default-badge">Default Address</span>
                <h3 className="address-name">{defaultAddress.fullName}</h3>
              </div>
              <p className="address-details">
                {defaultAddress.address1}<br />
                {defaultAddress.address2 && <>{defaultAddress.address2}<br /></>}
                {defaultAddress.city}, {defaultAddress.state} {defaultAddress.postalCode}<br />
                {defaultAddress.country}<br />
                {defaultAddress.phone && <>Phone: {defaultAddress.phone}</>}
              </p>
              <div className="address-card-actions">
                <button 
                  className="action-btn edit"
                  onClick={handleViewAllAddresses}
                >
                  Manage Addresses
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Profile;